import { useCallback, useContext, useMemo } from "react";
import { SnowPlowAnalyticsContext } from "../../snow-plow-analytics-context";
import { InitTrackerParams, TrackSelfDescribingEventParams } from "./types";

export const useSnowPlowAnalytics = () => {
  const snowPlowAnalyticsContext = useContext(SnowPlowAnalyticsContext);

  if (snowPlowAnalyticsContext === undefined) {
    throw new Error(
      "useSnowPlowAnalytics must be used within a SnowPlowAnalyticsProvider"
    );
  }

  const { isRpTrackerReady, isCMODigitalPersonalizationReady } =
    snowPlowAnalyticsContext;

  const safeCall = useCallback(
    <T extends Array<unknown>, U>(method: (...args: T) => U) =>
      function (...args: T) {
        if (!isRpTrackerReady) {
          console.error(
            "rpTracker is not available yet. You can check availability using the isRpTrackerReady flag"
          );
        } else {
          method(...args);
        }
      },
    [isRpTrackerReady]
  );

  const initTracker = useMemo(
    () =>
      safeCall(
        ({ delegateInfo, dboSectionActivityDictionary }: InitTrackerParams) => {
          window.rpTracker?.init?.({
            delegateInfo,
            dboSectionActivityDictionary,
          });
        }
      ),
    [safeCall]
  );

  const trackSelfDescribingEvent = useMemo(
    () =>
      safeCall(
        ({
          eventCategory,
          eventAction,
          eventValueText,
          eventJSON,
        }: TrackSelfDescribingEventParams) => {
          window.rpTracker?.trackSelfDescribingEvent?.(
            eventCategory,
            eventAction,
            eventValueText,
            eventJSON
          );
        }
      ),
    [safeCall]
  );

  return {
    isRpTrackerReady,
    isCMODigitalPersonalizationReady,
    initTracker,
    trackSelfDescribingEvent,
  };
};
